import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  ModalClose,
  ModalDialog,
  Typography,
} from "@mui/joy";
import { useMatchRoute, useNavigate } from "@tanstack/react-router";
import { ApiCompetition } from "griptonite-api-client";

import { useDeleteCompetitionMutation } from "@/hooks/competitions";
import { useCurrentVenueUuid } from "@/hooks/venues";

import { useModal } from "./useModal";

export default function ConfirmDeleteCompetition({ competition }: { competition: ApiCompetition }) {
  const { close } = useModal();
  const venueUuid = useCurrentVenueUuid();
  const leagueUuid = competition.league?.uuid;
  const mutation = useDeleteCompetitionMutation(venueUuid, competition.league?.uuid);

  const onConfirm = () =>
    mutation.mutate(competition.uuid, {
      onSuccess: () => {
        if (
          matchRoute({
            to: "/gym/$venueUuid/events/$competitionUuid",
            fuzzy: true,
          }) &&
          venueUuid
        ) {
          if (leagueUuid) {
            navigate({
              to: "/gym/$venueUuid/events/series/$leagueUuid",
              from: "/gym/$venueUuid/events/$competitionUuid",
              replace: true,
              params: {
                leagueUuid,
              },
            });
          } else {
            navigate({
              to: "/gym/$venueUuid/events",
              from: "/gym/$venueUuid/events/$competitionUuid",
              replace: true,
            });
          }
        }
        close();
      },
      onError: (_err) => {
        alert("Failed to delete competition. Please try again or contact support");
      },
    });

  const navigate = useNavigate();
  const matchRoute = useMatchRoute();

  const hasEntries = competition.entriesCount > 0;

  const canDelete = !hasEntries;

  return (
    <ModalDialog>
      <ModalClose />
      <DialogTitle>Are you sure?</DialogTitle>
      <DialogContent>
        <Typography sx={{ maxWidth: 350 }}>
          Deleting this competition will remove all categories, competition circuits and routes.{" "}
          <br />
          {canDelete ? (
            <strong>(Once a competition has entries, it cannot be deleted using this tool)</strong>
          ) : (
            <Alert color="danger" sx={{ marginTop: 2 }}>
              This competition has entries and cannot be deleted using this tool. Please contact
              support if you need to remove it.
            </Alert>
          )}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          color="danger"
          onClick={() => {
            onConfirm();
          }}
          sx={{ flex: 1 }}
          loading={mutation.isPending}
          disabled={!canDelete}
        >
          Continue
        </Button>
        <Button color="neutral" variant="soft" onClick={close} sx={{ flex: 1 }}>
          Cancel
        </Button>
      </DialogActions>
    </ModalDialog>
  );
}
